import { Dispatch, SetStateAction, PropsWithChildren, useState, useMemo, createContext, useEffect } from 'react';
import { _User, _UserType } from '../constants/staticTypes';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQueryClient } from 'react-query';
import {
    clearCognitoData,
    getUserDetailsByUserName,
    getUserPermissions,
    savePermissions,
    userCustomAttributeValues,
    userLogout,
    userSession,
} from '../helpers/authHelper';
import { routes } from '../routes/allRoutes';
import { ADMIN_ID_HEADER, BROADCAST_MESSAGES, LANGUAGE_CHANGE_TIME, ORG_ID_HEADER, TARGET_LANGUAGE } from '../constants/constants';
import { useEffectOnce } from 'react-use';
import MlxBlockUI from '../components/common/MlxBlockUI/MlxBlockUI';
import { CHANNEL, FORCE_LOGOUT } from '../constants/strings';

interface AuthContextValue {
    isSignedIn: boolean;
    setIsSignedIn: Dispatch<SetStateAction<boolean>>;
    userType: _UserType | null;
    setUserType: Dispatch<SetStateAction<_UserType | null>>;
    userData: _User | null;
    setUserData: Dispatch<SetStateAction<_User | null>>;
    isLoading: boolean;
    setIsLoading: Dispatch<SetStateAction<boolean>>;
    userPermissions: any;
    setUserPermissions: Dispatch<SetStateAction<any>>;
    isAdminToOrgAdmin: boolean;
    setIsAdminToOrgAdmin: Dispatch<SetStateAction<boolean>>;
}

export const AuthCtx = createContext<AuthContextValue>({
    isSignedIn: false,
    setIsSignedIn: () => {},
    userType: null,
    setUserType: () => {},
    userData: null,
    setUserData: () => {},
    isLoading: false,
    setIsLoading: () => {},
    userPermissions: {},
    setUserPermissions: () => {},
    isAdminToOrgAdmin: false,
    setIsAdminToOrgAdmin: () => {},
});

export const AuthCtxProvider = ({ children }: PropsWithChildren) => {
    const navigate = useNavigate();
    const queryClient = useQueryClient();

    const [isLoading, setIsLoading] = useState(true);
    const [isSignedIn, setIsSignedIn] = useState(false);
    const [userType, setUserType] = useState<_UserType | null>(null);
    const [userData, setUserData] = useState<_User | null>(null);
    const [userPermissions, setUserPermissions] = useState({});
    const [isAdminToOrgAdmin, setIsAdminToOrgAdmin] = useState(false); // state to identify if an admin is switching to as an org admin

    // Handle force logout using react query
    const userLogoutMutation = useMutation(userLogout, {
        onMutate: () => {
            setIsLoading(true);
        },
        onSuccess: () => {
            setIsSignedIn(false);
            setUserData(null);
            setUserType(null);
        },
        onSettled: () => {
            navigate(routes.login, {
                replace: true,
            });
            setIsLoading(false);
        },
    });

    // Handle published custom messages
    useEffect(() => {
        const handleMessage = (event: Event) => {
            const messageEvent = event as MessageEvent;

            if (messageEvent?.origin !== window.location.origin) return;

            switch (messageEvent?.data) {
                case BROADCAST_MESSAGES.FORCE_LOGOUT:
                    userLogoutMutation.mutate();
                    break;
                default:
                    break;
            }
        };

        window.addEventListener('message', handleMessage);
        return () => {
            window.removeEventListener('message', handleMessage);
        };
    }, [userLogoutMutation]);

    useEffectOnce(() => {
        setIsLoading(true);
        // check if already logged
        userSession()
            .then(async (response) => {
                // update admin id/org id
                const [userTypeValue, adminId, orgId] = await userCustomAttributeValues(response, [
                    'custom:userType',
                    'custom:userIdDb',
                    'custom:organizationId',
                ]);
                const userType = userTypeValue as _UserType;
                localStorage.setItem(ADMIN_ID_HEADER, adminId ?? '');
                if (!window.location.pathname.startsWith('/organization')) {
                    localStorage.setItem(ORG_ID_HEADER, orgId ?? '');
                }

                // user details
                const userDataResponse = await getUserDetailsByUserName(queryClient, userType, response.username);
                const userData = userDataResponse.data.data as _User;

                // setting up user permissions
                const userPermissions = getUserPermissions(userData);
                if (Object.keys(userPermissions).length > 0) {
                    savePermissions(queryClient, userPermissions);
                    // showLoader(queryClient, false)
                } else {
                    // create a new broadcast channel with the same name
                    const channel = new BroadcastChannel(CHANNEL);
                    channel.postMessage(FORCE_LOGOUT);
                    channel.close();
                }

                // Update language
                localStorage.setItem(TARGET_LANGUAGE, userData?.preferredLangKey ?? 'en');
                localStorage.setItem(LANGUAGE_CHANGE_TIME, new Date().getTime().toString());

                // save the organization details if adminStaff
                if (userType === 'adminStaff') {
                    localStorage.setItem('adminToOrgPermissionDetails', JSON.stringify(userData?.organizations));
                }

                setIsSignedIn(true);
                setUserType(userType);
                setUserData(userData);
                setUserPermissions(userPermissions);

                const hasIsAdminToOrgAdmin = Object.keys(localStorage).some((key) => key.includes('isAdminToOrgAdmin'));
                if (hasIsAdminToOrgAdmin) {
                    const isSuperAdmin = localStorage.getItem('isAdminToOrgAdmin') === 'true';
                    isSuperAdmin ? setIsAdminToOrgAdmin(true) : setIsAdminToOrgAdmin(false);
                }

                // If org admin and don't have any venue, redirect to venue/tenant onboarding
                if (userType === 'organizationAdmin') {
                    if (userData?.venueCount <= 0) {
                        navigate(routes.onBoardCreateVenue + 'step2', { replace: true });
                        return;
                    }
                }

                // TODO: handle route path by url
                // navigate(routes.dashboard, { replace: true });
            })
            .catch(() => {
                clearCognitoData();
            })
            .finally(() => setIsLoading(false));
    });

    const authContextValue = useMemo(() => {
        return {
            isSignedIn,
            setIsSignedIn,
            userType,
            setUserType,
            userData,
            setUserData,
            isLoading,
            setIsLoading,
            userPermissions,
            setUserPermissions,
            isAdminToOrgAdmin,
            setIsAdminToOrgAdmin,
        };
    }, [isSignedIn, userType, userData, isLoading, userPermissions, isAdminToOrgAdmin]);

    if (isLoading) {
        return <MlxBlockUI isLoading={true}></MlxBlockUI>; // or a loading spinner
    }

    return <AuthCtx.Provider value={authContextValue}>{children}</AuthCtx.Provider>;
};
